import { inject } from '@angular/core';
import { CanActivateChildFn, Router, RouterStateSnapshot } from '@angular/router';
import { catchError, filter, from, map, of, switchMap, take } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { GlobalesService } from '../services/globales.service';
import { MainService } from '../api/main.service';

// Páginas por las que el usuario tiene que poder pasar para poder solicitar
// y completar la activación de las copias de seguridad. Si las excluyéramos
// del guard se produciría un bucle infinito de redirecciones.
const RUTAS_EXENTAS = [
  '/login',
  '/gestion-datos/solicitud-acceso',
  '/gestion-datos/validacion-otp',
  '/gestion-datos/gestion-datos',

  // Estas dos no se navegan desde el propio menú de la app: se cargan
  // externamente (iframe/enlace QR de la intranet) con un token en la
  // query string (?tkn=...), sin pasar por el login normal de la app.
  // No tiene sentido interceptarlas con el aviso de copias de seguridad.
  '/preview-inspeccion',
  '/subir-fotos-inspeccion'
]

export const BackupGuard: CanActivateChildFn = (_route, state: RouterStateSnapshot) => {
  const router = inject(Router)
  const globales = inject(GlobalesService)
  const service = inject(MainService)

  // En nativo el flujo es otro (QR) y las copias de seguridad automáticas
  // (File System Access API) no aplican, igual que en AuthGuard.
  if (Capacitor.isNativePlatform()) return true

  const url = state.url.split('?')[0]
  if (RUTAS_EXENTAS.includes(url)) return true

  // globales.backupConfigurado empieza en -1 mientras AppComponent comprueba
  // de forma asíncrona el estado de las copias de seguridad. Esperamos a que
  // resuelva antes de decidir si se puede continuar la navegación.
  return globales.backupConfigurado.pipe(
    filter(estado => estado !== -1),
    take(1),
    switchMap(estado => {

      // Las copias ya están activas: no hace falta consultar la fecha de aviso.
      if (estado === 1) return of(true)

      // Solo redirigimos si, además de no estar activas, ya se ha alcanzado
      // la fecha de aviso configurada en servidor. getTime() compara instantes
      // absolutos, así que la comparación ya es correcta en UTC sin conversiones.
      return from(service.Datos.getFechaAvisoCopiasSeguridad()).pipe(
        map(fechaAviso => {
          if (fechaAviso != null && new Date(fechaAviso).getTime() < Date.now()) {
            return router.createUrlTree(['/gestion-datos/solicitud-acceso'])
          }
          return true
        }),
        // Si falla la consulta de la fecha de aviso no podemos confirmar la
        // condición, así que preservamos la navegación tal cual estaba.
        catchError(() => of(true))
      )
    })
  )
}
